import mixpanel from 'mixpanel-browser';
import { useCallback } from 'react';
import { useClient } from 'urql';

import {
  AiSearchTrackingEvents,
  FeatureFlag,
  TaxonomyTrackingEvents,
} from '@revelio/core';
import type {
  CompanyDiscoveryAiOutput,
  CustomRoleTaxonomySelection,
  TalentDiscoveryAiFilterSearchResponseV2,
} from '@revelio/data-access';

import { useUnleashFlag } from '../../../../shared/unleash-flags/use-unleash-flag';
import { mapTaxonomyIdToSelection } from '../../../../shared/user-preferences/utils/taxonomy-mapper';
import {
  usePageConfig,
  useTabContentConfig,
} from '../../components/PageConfigProvider/PageConfigProvider';
import { RevelioPageId } from '../../components/PageConfigProvider/types';
import { isScreenerV2Page } from '../../utils/routing-helpers';
import { FILTER_DEPENDENCIES } from '../constants/filterDependencies';
import { createDefaultFilterMap } from '../contexts/defaultFilters';
import { useFilterContext } from '../contexts/FilterProvider';
import { roleTaxonomyDefinition } from '../definitions/roleTaxonomy';
import type { CustomSkillTaxonomySelection } from '../definitions/skillTaxonomy';
import {
  generatePageKey,
  NO_PRIMARY_FILTER_KEY,
} from '../engine/filterGroupConsistency';
import { getRoleTaxonomyValue } from '../hooks/useAdaptiveRoleTaxonomy';
import { getSkillTaxonomyValue } from '../hooks/useAdaptiveSkillTaxonomy';
import { useAiSearchCompatibilityToast } from '../hooks/useAiSearchCompatibilityToast';
import { useAllSelectionLists } from '../hooks/useAllSelectionLists';
import { useViewDateGranularity } from '../hooks/useViewDateGranularity';
import {
  type Filter,
  FilterId,
  type FilterSelectionsMap,
  FilterType,
  isMultiFilter,
  isSingleValueFilter,
  type MultiFilter,
  type TreeFilter,
} from '../types/filters';
import { patchTreeFilterNames } from '../utils/patchTreeFilterNames';
import { adaptAiSearchFiltersForPage } from './adaptAiSearchFiltersForPage';
import {
  collectUnresolvedCompanyRcids,
  resolveCompanyNamesFromApi,
} from './resolveUnknownCompanyNames';
import { transformAiResponseToCustomDashFilters } from './transformAiResponseToCustomDashFilters';
import type { AiTransformUnknownFilter } from './transformAiResponseToCustomDashFilters/types';
import type { ScreenerAiTransformUnknownFilter } from './transformScreenerAiResponseToCustomDashFilters';
import { transformScreenerAiResponseToCustomDashFilters } from './transformScreenerAiResponseToCustomDashFilters';

/**
 * The AI apply dispatches REPLACE_FILTERS wholesale, bypassing the reducer's
 * filter-dependency processing. Re-apply the DATE_RANGE dependencies here so the
 * AI date lands the same way a manual edit does — notably syncing SNAPSHOT_DATE
 * to the range's start (min), so composition/snapshot pages show the AI date on
 * their snapshot axis instead of nothing. Only touches dependent date filters the
 * page already has, so unrelated pages are unaffected.
 */
const syncDateFilterDependencies = (filterMap: FilterSelectionsMap): void => {
  const dateRange = filterMap.get(FilterId.DATE_RANGE);
  if (!dateRange || dateRange.type !== FilterType.DATE_RANGE) {
    return;
  }
  const dependencies = FILTER_DEPENDENCIES.get(FilterId.DATE_RANGE) ?? [];
  for (const dep of dependencies) {
    const existing = filterMap.get(dep.dependentFilterId);
    if (!existing) {
      continue;
    }
    const update = dep.transformer(dateRange, existing, filterMap);
    if (update) {
      filterMap.set(dep.dependentFilterId, {
        ...existing,
        ...update,
        isUserSubmitted: true,
        isVisible: true,
      } as Filter);
    }
  }
};

export type ApplyAiSearchFiltersResult = {
  adaptedFilters: Filter[];
  unknownFilters: AiTransformUnknownFilter[];
};

export type ApplyScreenerAiSearchFiltersResult = {
  adaptedFilters: Filter[];
  unknownFilters: ScreenerAiTransformUnknownFilter[];
};

const isMultiCompanyFilter = (
  filter: Filter
): filter is MultiFilter<TreeFilter> =>
  isMultiFilter(filter) && filter.wrappedFilterId === FilterId.COMPANY;

/**
 * The company selection used to derive the adaptive role taxonomy — the same
 * input `useAdaptiveRoleTaxonomy`/`useAdaptiveSkillTaxonomy` read from the
 * applied filter map. A MULTI_COMPANY wrapper is flattened into one synthetic
 * tree filter so all company/industry selections contribute to the taxonomy.
 */
const getCompanyFilterForTaxonomy = (
  filterMap: FilterSelectionsMap
): TreeFilter | undefined => {
  const multi = filterMap.get(FilterId.MULTI_COMPANY);
  if (multi && isMultiCompanyFilter(multi)) {
    const first = multi.instances[0];
    if (!first) return undefined;
    return {
      ...first,
      selections: multi.instances.flatMap((inst) => inst.selections),
    };
  }
  const single = filterMap.get(FilterId.COMPANY);
  return single && single.type === FilterType.TREE ? single : undefined;
};

const getConsistencyKeyForAiSearch = (
  revelioPageId: RevelioPageId | string | undefined,
  primaryFilterConfig: { id: string } | undefined
): string => {
  if (isScreenerV2Page(revelioPageId) && revelioPageId) {
    return revelioPageId;
  }
  return primaryFilterConfig?.id ?? NO_PRIMARY_FILTER_KEY;
};

/**
 * Returns a callback that applies an AI Search response to the filter state:
 * transform -> role taxonomy injection -> base filter map -> adapt -> resolve names -> dispatch.
 * Callers can use the returned { adaptedFilters, unknownFilters } for context-specific
 * updates (e.g. AiSearchContext state, highlighting overlay).
 */
export const useApplyAiSearchFilters = () => {
  const {
    dispatch,
    getFilterData,
    activeFilterMap,
    defaultSelectionOverrides,
    filterState,
  } = useFilterContext();
  const selectionLists = useAllSelectionLists();
  const gqlClient = useClient();
  const { revelioPageId, primaryFilter: primaryFilterConfig } =
    useTabContentConfig();
  const { config: pageConfig } = usePageConfig();
  const showCompatibilityToast = useAiSearchCompatibilityToast();
  const viewGranularity = useViewDateGranularity();

  const applyAiSearchFilters = useCallback(
    async (
      response: TalentDiscoveryAiFilterSearchResponseV2,
      options?: {
        /** Defaults to `merge` so AI primary selections combine with page defaults (overflow deactivated). Pass `replace` to wipe defaults. */
        primarySelectionMergeMode?: 'merge' | 'replace';
        /**
         * When true, do not coerce an unknown `revelioPageId` to Talent Discovery
         * inside `adaptAiSearchFiltersForPage` (e.g. global-assistant chart preview).
         */
        skipTalentDiscoveryAdaptFallback?: boolean;
        /**
         * When true, seed the filter map from the page's EXISTING applied
         * filters (the consistency group `filterState.groupMap.get(consistencyKey)`,
         * the same source the page's filter chips render from) instead of
         * defaults, so this apply ADDS to / overrides the current filters
         * rather than replacing the whole set. Falls back to defaults when the
         * group has no applied filters yet.
         */
        mergeWithExistingFilters?: boolean;
        /**
         * When true, pair AI-resolved role ids with the taxonomy they were
         * actually resolved under (the numeric `response.filters.roles_tax_id`)
         * and mark the resulting ROLE_TAXONOMY filter
         * `isAiApplied` so the preference sync cannot re-clobber the pairing.
         * Role ids are taxonomy-relative — querying them under any other
         * taxonomy returns wrong data or an empty-set backend rejection.
         *
         * DELIBERATELY opt-in. Two callers set it: the global-assistant chart
         * preview scope (its hidden data fetch MUST pair correctly) and the
         * bridge's resolved-artifact-link apply (the user clicked "show me
         * this answer's filters", so the destination page adopting the
         * answer's taxonomy is the correct outcome). On blanket AI page
         * applies the same pairing would visibly switch the page's taxonomy
         * selection away from the user's preference — a product decision not
         * yet made, so those keep their historical (unpaired) behavior (see
         * the AI-search taxonomy-contract follow-up).
         */
        pairRoleTaxonomyWithResponse?: boolean;
      }
    ): Promise<ApplyAiSearchFiltersResult> => {
      const { filters: customDashFilters, unknownFilters } =
        transformAiResponseToCustomDashFilters(
          response,
          selectionLists,
          getFilterData
        );

      const hasRoleInstance = customDashFilters.some(
        (f) => f.id === FilterId.ROLE || f.id === FilterId.MULTI_ROLE
      );
      const hasRoleTaxonomyInstance = customDashFilters.some(
        (f) => f.id === FilterId.ROLE_TAXONOMY
      );

      // Compute the consistency (group) key and page key up front: the
      // additive seed below needs the group key to locate the page's
      // EXISTING applied filters. The group map is keyed by the consistency
      // key — which is `primaryFilterConfig.id` for company/geography/role
      // pages, the page id for screener-v2 pages, and `NO_PRIMARY_FILTER_KEY`
      // for pages with no primary filter (e.g. Talent Discovery, whose
      // applied role/company/seniority/gender filters all live under
      // `NO_PRIMARY_FILTER_KEY`). Keying by `primaryFilterConfig.id` alone
      // missed that group entirely on discovery pages, so a refinement seeded
      // from defaults and wiped the prior filters.
      const consistencyKey = getConsistencyKeyForAiSearch(
        revelioPageId,
        primaryFilterConfig
      );
      const pageKey = generatePageKey(
        consistencyKey,
        revelioPageId ?? pageConfig.id
      );

      // IMPORTANT: when resolving taxonomy filters to carry over into this
      // apply, read from the TARGET group's filter state — NOT from
      // `activeFilterMap` (which is the currently-ACTIVE group's map, and
      // during navigation is still the previous page's group). Using
      // `activeFilterMap` causes a cross-scope leak: e.g. navigating
      // COMPANY → GEOGRAPHY writes `{default: true, isUserSubmitted: true}`
      // into the GEOGRAPHY group via pref sync; then when the user
      // navigates back to COMPANY, `activeFilterMap` is still the GEOGRAPHY
      // group, we'd preserve its `{default: true, isUserSubmitted: true}`
      // into the REPLACE_FILTERS payload targeting the COMPANY group, and
      // `filtersToPreferences` on COMPANY then infers NOT-adaptive (because
      // `isUserSubmitted: true`), making the Preferences UI report the
      // user's COMPANY pref as "default" even though it's actually
      // "adaptive" on the server.
      //
      // Reading from the target group (by consistency key) preserves each
      // group's own taxonomy state independently, and falls back to
      // `baseFilterMap` defaults when the group hasn't been visited yet.
      const targetGroupFilterMap = filterState.groupMap.get(consistencyKey);

      if (hasRoleInstance && !hasRoleTaxonomyInstance) {
        // Ensure a ROLE_TAXONOMY filter travels alongside the AI-applied role.
        // Preserve the target group's existing filter AS-IS — critically
        // including `isUserSubmitted` (flipping it to `true` locks the page out
        // of adaptive mode). The adaptive VALUE itself is (re)derived from the
        // final company selection in the post-assembly block below, once
        // `adaptAiSearchFiltersForPage` has rolled-up/normalized the company
        // tree — reading the raw pre-adapt selection here gives the wrong RICS
        // level.
        const existingRoleTaxonomy = targetGroupFilterMap?.get(
          FilterId.ROLE_TAXONOMY
        );
        const hasUsableExistingTaxonomy =
          existingRoleTaxonomy &&
          existingRoleTaxonomy.type === FilterType.VALUE &&
          'value' in existingRoleTaxonomy;
        // Role ids are TAXONOMY-RELATIVE, but pairing them with the response's
        // taxonomy happens ONCE, on the FINAL assembled map (the
        // `pairRoleTaxonomyWithResponse` block below) — pairing the pushed
        // filter here is pointless because `adaptAiSearchFiltersForPage` and
        // the carry-over seeding can strip or clobber it before dispatch.
        customDashFilters.push(
          hasUsableExistingTaxonomy
            ? existingRoleTaxonomy
            : {
                ...roleTaxonomyDefinition,
                isUserSubmitted: true,
              }
        );
      }

      const baseFilterMap =
        options?.mergeWithExistingFilters && targetGroupFilterMap
          ? new Map(targetGroupFilterMap)
          : createDefaultFilterMap(
              primaryFilterConfig?.id,
              undefined,
              defaultSelectionOverrides
            );
      // Carry over the target group's taxonomy AND correction-preference
      // filters. Corrections (DATA_ADJUSTMENTS / CONTINGENT_WORKERS) are synced
      // per-scope from user preferences; `createDefaultFilterMap` resets them to
      // empty, so without this an AI apply leaves them unpopulated and
      // composition charts that gate on `correctionPreferencesSynced` never
      // leave their loading state.
      [
        FilterId.ROLE_TAXONOMY,
        FilterId.SKILL_TAXONOMY,
        FilterId.DATA_ADJUSTMENTS,
        FilterId.CONTINGENT_WORKERS,
      ].forEach((filterId) => {
        const existing = targetGroupFilterMap?.get(filterId);
        if (existing) {
          baseFilterMap.set(filterId, existing);
        }
      });

      const currentPrimaryFilter = (() => {
        if (!primaryFilterConfig) return undefined;
        // Same reasoning as above: read the target group's primary filter
        // state, not the active (mid-nav, still previous scope's) map.
        const existing =
          targetGroupFilterMap?.get(primaryFilterConfig.id) ??
          baseFilterMap.get(primaryFilterConfig.id);
        if (
          !existing ||
          existing.type !== FilterType.TREE ||
          !existing.isPrimary
        ) {
          return existing;
        }
        const pageSelections = filterState.pageMap
          .get(pageKey)
          ?.get(primaryFilterConfig.id);
        if (!pageSelections) return existing;
        return { ...existing, primarySelections: pageSelections };
      })();

      const adaptRevelioPageId =
        options?.skipTalentDiscoveryAdaptFallback === true
          ? revelioPageId
          : (revelioPageId ?? RevelioPageId.TALENT_DISCOVERY);

      const { adaptedFilters, modifications } = adaptAiSearchFiltersForPage({
        filters: customDashFilters,
        revelioPageId: adaptRevelioPageId,
        primaryFilterConfig,
        currentPrimaryFilter,
        primarySelectionMergeMode:
          options?.primarySelectionMergeMode ?? 'merge',
        viewGranularity,
      });

      if (modifications.length > 0) {
        showCompatibilityToast(modifications);
      }

      const filterMap: FilterSelectionsMap = new Map(baseFilterMap);
      adaptedFilters.forEach((filter) => {
        filterMap.set(filter.id, filter);
      });

      // Sync SNAPSHOT_DATE ↔ DATE_RANGE (REPLACE_FILTERS below skips the reducer's
      // dependency processing), so the AI date lands on both the range and
      // snapshot axes.
      syncDateFilterDependencies(filterMap);

      // Re-assert the response taxonomy AFTER assembly: the pushed filter above
      // can be stripped by `adaptAiSearchFiltersForPage` (page-compat filtering)
      // and the carry-over block re-seeds the group's preference-synced value
      // into `baseFilterMap` — either way the ids' true taxonomy loses to a
      // stale one at query time (observed live: role_taxonomy 62 with ids
      // resolved under 0). Setting it on the FINAL map (mirroring the adaptive
      // block below) is the only ordering that sticks. The adaptive company
      // block after this still takes precedence when a company selection
      // exists — that flow re-resolves ids under its corrected taxonomy.
      // `isAiApplied` keeps `SyncInitialPreferencesToFilters` from re-syncing
      // the user's taxonomy preference OVER this value after the dispatch below
      // (the pref-sync effect lands later in a freshly-mounted scope, e.g. the
      // assistant chart preview, and would unpair the ids from their taxonomy).
      if (hasRoleInstance && options?.pairRoleTaxonomyWithResponse === true) {
        // Use the NUMERIC `roles_tax_id`: the backend contract guarantees it
        // states the taxonomy the ids were ACTUALLY resolved under (the string
        // `roles_tax` merely echoes the request, and live captures showed the
        // parser can fall back to a different taxonomy than requested). 0 is a
        // valid value (= default taxonomy).
        const taxId = response.filters.roles_tax_id;
        const finalResponseTaxonomy =
          typeof taxId === 'number' && Number.isFinite(taxId)
            ? mapTaxonomyIdToSelection(taxId)
            : undefined;
        const assembledRoleTaxonomy = filterMap.get(FilterId.ROLE_TAXONOMY);
        if (
          finalResponseTaxonomy &&
          assembledRoleTaxonomy &&
          isSingleValueFilter<CustomRoleTaxonomySelection>(
            assembledRoleTaxonomy
          )
        ) {
          filterMap.set(FilterId.ROLE_TAXONOMY, {
            ...assembledRoleTaxonomy,
            value: {
              ...finalResponseTaxonomy,
              version: assembledRoleTaxonomy.value?.version,
            },
            isUserSubmitted: false,
            isAiApplied: true,
          });
        }
      }

      // Adaptive role + skill taxonomy normally (re)computes in the adaptive
      // hooks AFTER render, but useAdaptiveRoleTaxonomy FREEZES the taxonomy
      // whenever a role is selected (recomputing would reshape the user's role
      // picks). On an AI apply the role and its taxonomy arrive together from the
      // SAME new company/industry, so the freeze would otherwise pin the taxonomy
      // to the PREVIOUS company's value (and the role label is taxonomy-dependent,
      // so it mislabels the role too). Resolve BOTH role and skill here, under the
      // same `hasRoleInstance` gate, so they always move together. Each is derived
      // from the applied company filter — the same input the post-render hooks
      // read — and resolves identically: a company carries its RICS in the
      // path-encoded id (`${k10}_${k400}_${rcid}`) the transform built from the
      // mapper, and a k400 industry rolls up to its k50 parent. The post-render
      // skill hook then recomputes the same value, so it never clobbers this.
      if (hasRoleInstance) {
        const appliedCompanyForTaxonomy =
          getCompanyFilterForTaxonomy(filterMap);
        if (
          appliedCompanyForTaxonomy &&
          appliedCompanyForTaxonomy.selections.length > 0
        ) {
          // Cast is safe: the iso-utility Item[] from useAllSelectionLists and
          // the local SelectionList<'rics_k400'> are the same RICS tree data.
          const ricsK400List = (selectionLists.find((l) => l.id === 'rics_k400')
            ?.value ?? []) as unknown as Parameters<
            typeof getRoleTaxonomyValue
          >[1];

          const appliedRoleTaxonomy = filterMap.get(FilterId.ROLE_TAXONOMY);
          if (
            appliedRoleTaxonomy &&
            isSingleValueFilter<CustomRoleTaxonomySelection>(
              appliedRoleTaxonomy
            ) &&
            !appliedRoleTaxonomy.isUserSubmitted
          ) {
            const feValue = getRoleTaxonomyValue(
              appliedCompanyForTaxonomy,
              ricsK400List
            );
            filterMap.set(FilterId.ROLE_TAXONOMY, {
              ...appliedRoleTaxonomy,
              value: { ...feValue, version: appliedRoleTaxonomy.value.version },
              isUserSubmitted: false,
            });
            mixpanel.track(TaxonomyTrackingEvents.ADAPTIVE_TAXONOMY_APPLIED, {
              taxonomy_type: 'role',
              applied_from_company: true,
              is_talent_discovery: true,
              page_id: revelioPageId ?? pageConfig.id,
            });
          }

          const appliedSkillTaxonomy = filterMap.get(FilterId.SKILL_TAXONOMY);
          if (
            appliedSkillTaxonomy &&
            isSingleValueFilter<CustomSkillTaxonomySelection>(
              appliedSkillTaxonomy
            ) &&
            !appliedSkillTaxonomy.isUserSubmitted
          ) {
            const feSkillValue = getSkillTaxonomyValue(
              appliedCompanyForTaxonomy,
              ricsK400List
            );
            filterMap.set(FilterId.SKILL_TAXONOMY, {
              ...appliedSkillTaxonomy,
              value: {
                ...feSkillValue,
                version: appliedSkillTaxonomy.value.version,
              },
              isUserSubmitted: false,
            });
            mixpanel.track(TaxonomyTrackingEvents.ADAPTIVE_TAXONOMY_APPLIED, {
              taxonomy_type: 'skill',
              applied_from_company: true,
              is_talent_discovery: true,
              page_id: revelioPageId ?? pageConfig.id,
            });
          }
        }
      }

      const unresolvedRcids = collectUnresolvedCompanyRcids(adaptedFilters);

      if (unresolvedRcids.length > 0) {
        try {
          const nameMap = await resolveCompanyNamesFromApi(
            gqlClient,
            unresolvedRcids
          );

          if (nameMap.size > 0) {
            const companyFilterId = filterMap.has(FilterId.MULTI_COMPANY)
              ? FilterId.MULTI_COMPANY
              : FilterId.COMPANY;
            const companyFilter = filterMap.get(companyFilterId);

            if (companyFilter) {
              filterMap.set(
                companyFilterId,
                isMultiCompanyFilter(companyFilter)
                  ? {
                      ...companyFilter,
                      instances: companyFilter.instances.map((inst) =>
                        patchTreeFilterNames(inst, nameMap)
                      ),
                    }
                  : patchTreeFilterNames(companyFilter, nameMap)
              );
            }
          }
        } catch (err) {
          console.warn(
            '[AI Search] Failed to resolve company names from API, falling back to numeric IDs',
            err
          );
        }
      }

      dispatch({
        type: 'REPLACE_FILTERS',
        payload: {
          filterMap,
          pageKey,
          skipReconciliation: true,
        },
      });

      const mergedUnknownFilters = [
        ...(response.unknownFilters || []),
        ...unknownFilters,
      ];

      const resolvedFilters = adaptedFilters.map(
        (f) => filterMap.get(f.id) ?? f
      );

      mixpanel.track(AiSearchTrackingEvents.AI_SEARCH_APPLIED, {
        method: 'ai',
        is_talent_discovery: true,
        filter_count: resolvedFilters.length,
        unknown_filter_count: mergedUnknownFilters.length,
        page_id: revelioPageId ?? pageConfig.id,
      });

      return {
        adaptedFilters: resolvedFilters,
        unknownFilters: mergedUnknownFilters,
      };
    },
    [
      selectionLists,
      getFilterData,
      defaultSelectionOverrides,
      filterState,
      primaryFilterConfig,
      revelioPageId,
      viewGranularity,
      pageConfig.id,
      showCompatibilityToast,
      dispatch,
      gqlClient,
    ]
  );

  const isValuationEnabled = useUnleashFlag(
    FeatureFlag.CompanyDiscoveryValuationFilter
  );
  const applyScreenerAiSearchFilters = useCallback(
    (
      response: CompanyDiscoveryAiOutput,
      options?: {
        /**
         * When true, seed from the page's CURRENT filters so this apply ADDS
         * to / overrides them rather than replacing the whole set — the
         * additive refinement path, mirroring `applyAiSearchFilters`.
         */
        mergeWithExistingFilters?: boolean;
      }
    ): ApplyScreenerAiSearchFiltersResult => {
      const { filters: rawCustomDashFilters, unknownFilters } =
        transformScreenerAiResponseToCustomDashFilters(response);

      const customDashFilters = isValuationEnabled
        ? rawCustomDashFilters
        : rawCustomDashFilters.filter((f) => f.id !== FilterId.VALUATION);

      // Compute the consistency (group) key + page key up front: the additive
      // seed and the taxonomy carry-over both need the group key to locate the
      // page's EXISTING filters. The group map is keyed by the consistency key —
      // the page id for screener-v2 pages, `primaryFilterConfig.id` otherwise
      // (see `applyAiSearchFilters` for the full rationale). Keying the seed by
      // `primaryFilterConfig.id` alone missed the screener-v2 group entirely
      // (its key is the page id), so a refinement seeded from defaults and wiped
      // the page's prior filters.
      const consistencyKey = getConsistencyKeyForAiSearch(
        revelioPageId,
        primaryFilterConfig
      );
      const pageKey = generatePageKey(
        consistencyKey,
        revelioPageId ?? pageConfig.id
      );

      // Seed the additive merge from the TARGET group's filters (keyed by the
      // consistency key), not `activeFilterMap` — which during navigation is
      // still the previous page's group (cross-scope leak). Falls back to
      // `activeFilterMap` when the group hasn't been populated yet.
      const targetGroupFilterMap = filterState.groupMap.get(consistencyKey);
      const baseFilterMap = options?.mergeWithExistingFilters
        ? new Map(targetGroupFilterMap ?? activeFilterMap)
        : createDefaultFilterMap(
            primaryFilterConfig?.id,
            undefined,
            defaultSelectionOverrides
          );
      // Carry over the target group's taxonomy filters (read from the target
      // group, not `activeFilterMap`, for the same cross-scope reason).
      [FilterId.ROLE_TAXONOMY, FilterId.SKILL_TAXONOMY].forEach((filterId) => {
        const existing = targetGroupFilterMap?.get(filterId);
        if (existing) {
          baseFilterMap.set(filterId, existing);
        }
      });

      const filterMap: FilterSelectionsMap = new Map(baseFilterMap);
      customDashFilters.forEach((filter) => {
        filterMap.set(filter.id, filter);
      });

      dispatch({
        type: 'REPLACE_FILTERS',
        payload: {
          filterMap,
          pageKey,
          skipReconciliation: true,
        },
      });

      mixpanel.track(AiSearchTrackingEvents.AI_SEARCH_APPLIED, {
        method: 'ai',
        is_talent_discovery: false,
        filter_count: customDashFilters.length,
        unknown_filter_count: unknownFilters.length,
        page_id: revelioPageId ?? pageConfig.id,
      });

      return {
        adaptedFilters: customDashFilters,
        unknownFilters,
      };
    },
    [
      activeFilterMap,
      filterState,
      defaultSelectionOverrides,
      primaryFilterConfig,
      revelioPageId,
      pageConfig.id,
      dispatch,
      isValuationEnabled,
    ]
  );

  return { applyAiSearchFilters, applyScreenerAiSearchFilters };
};
